// services/upload.js - Version améliorée
import axios from 'axios';
import api from './api';
import toast from 'react-hot-toast';

const uploadService = {
  // Configuration des limites par type
  limits: {
    photo: { maxSize: 10 * 1024 * 1024, types: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] },
    video: { maxSize: 100 * 1024 * 1024, types: ['video/mp4', 'video/quicktime', 'video/x-msvideo'] },
    chat: { 
      image: { maxSize: 10 * 1024 * 1024, types: ['image/jpeg', 'image/png', 'image/gif'] },
      video: { maxSize: 50 * 1024 * 1024, types: ['video/mp4', 'video/quicktime'] }
    }
  },

  /**
   * Valide un fichier selon le type
   */
  validateFile(file, type, subType = null) {
    if (!file) {
      throw new Error('Aucun fichier sélectionné');
    }

    let config;
    if (type === 'chat') {
      const mediaType = file.type.startsWith('image/') ? 'image' : 'video';
      config = this.limits.chat[mediaType];
      if (!config) {
        throw new Error('Type de média non supporté');
      }
    } else {
      config = this.limits[type];
    }

    if (!config) {
      throw new Error('Type d\'upload invalide');
    }

    // Vérifier le type MIME
    if (!config.types.includes(file.type)) {
      const typesStr = config.types.map(t => t.split('/')[1].toUpperCase()).join(', ');
      throw new Error(`Format non supporté. Utilisez: ${typesStr}`);
    }

    // Vérifier la taille
    if (file.size > config.maxSize) {
      const sizeMB = Math.round(config.maxSize / (1024 * 1024));
      throw new Error(`Le fichier ne doit pas dépasser ${sizeMB}MB`);
    }

    return true;
  },

  /**
   * Upload une photo
   */
 // services/upload.js
uploadPhoto: async (file, type = 'photo', isPrivate = false, onProgress = null) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!user?.id) {
      return { success: false, error: 'Utilisateur non connecté' };
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('user_id', user.id); // AJOUT IMPORTANT
    
    if (type === 'photo') {
      formData.append('is_private', isPrivate ? '1' : '0');
    }

    const response = await api.post('/upload.php', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'X-User-ID': user.id // Aussi dans les headers pour sécurité
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });

    return response.data;
  } catch (error) {
    console.error('Upload photo error:', error);
    return { 
      success: false, 
      error: error.response?.data?.error || error.message 
    };
  }
},

  /**
   * Upload une vidéo
   */
 // services/upload.js - Extrait modifié

// services/upload.js
uploadVideo: async (file, titre, isPrivate = false, onProgress = null) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!user?.id) {
      return { success: false, error: 'Utilisateur non connecté' };
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'video');
    formData.append('titre', titre.trim());
    formData.append('is_private', isPrivate ? '1' : '0');
    formData.append('user_id', user.id);

    // Créer une nouvelle instance axios avec HTTP/1.1 forcé
    const response = await axios.create({
      baseURL: process.env.REACT_APP_AJAX_URL,
      timeout: 300000, // 5 minutes
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    }).post('/upload.php', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
        'Connection': 'close'  // Force la fermeture HTTP/2 -> fallback HTTP/1.1
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      }
    });

    return response.data;
  } catch (error) {
    console.error('Upload error:', error);
    return { success: false, error: error.message };
  }
},

  /**
   * Upload un média pour le chat
   */
  uploadChatMedia: async (file, receiverId, onProgress = null) => {
    try {
      // Validation
      if (!receiverId) {
        throw new Error('Destinataire non spécifié');
      }

      uploadService.validateFile(file, 'chat');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'chat');
      formData.append('receiver_id', receiverId.toString());

      const response = await api.post('/upload-chat-media.php', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        }
      });

      if (response.data.success) {
        return {
          success: true,
          filename: response.data.filename,
          media_type: response.data.media_type,
          message_id: response.data.message_id,
          message: 'Média envoyé'
        };
      } else {
        throw new Error(response.data.error || 'Erreur upload');
      }
    } catch (error) {
      console.error('Upload chat error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Supprime un fichier
   */
  deleteFile: async (filename, type) => {
    try {
      if (!filename || !type) {
        throw new Error('Paramètres manquants');
      }

      const response = await api.post('/delete-file.php', {
        filename: filename,
        type: type
      });

      if (response.data.success) {
        return {
          success: true,
          message: 'Fichier supprimé'
        };
      } else {
        throw new Error(response.data.error || 'Erreur suppression');
      }
    } catch (error) {
      console.error('Delete file error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default uploadService;